import { useEffect, useState, useRef, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useStore } from "../../store";
import { perspectives } from "../../constants";
import Header from "./Header";
import type { TreeNode } from "../routes/treeBuilder";
import Sidebar from "./Sidebar";
import LicenseNotice from "./LicenseNotice";
import { isModuleLicensed } from "../utils/license";

export default function Layout({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentPerspective, setPerspective, treeData, setSelectedNodeId, userProfile } = useStore();
  const visiblePerspectives = useMemo(
    () => perspectives.filter((p) => isModuleLicensed(userProfile, p.module)),
    [userProfile],
  );
  const [searchText, setSearchText] = useState("");
  const [_filteredTree, setFilteredTree] = useState<TreeNode[]>([]);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const lastPerspective = useRef<string | null>(null);

  // Reset lastPerspective on mount
  useEffect(() => {
    lastPerspective.current = null;
  }, []);

  // Sync URL -> Zustand only when perspective changes
  useEffect(() => {
    const segments = location.pathname.split("/").filter(Boolean);
    const perspective = segments[0] || "administration";

    // Only update perspective if it actually changed
    if (perspective !== lastPerspective.current) {
      lastPerspective.current = perspective;
      setPerspective(perspective);
      setSearchText("");
    }
  }, [location.pathname, setPerspective]);

  useEffect(() => {
    if (!searchText.trim()) {
      setFilteredTree(treeData);
    } else {
      const lower = searchText.toLowerCase();
      const filtered = treeData.filter((node) =>
        node.name?.toLowerCase().includes(lower)
      );
      setFilteredTree(filtered);
    }
  }, [searchText, treeData]);

  // Count leaf nodes (actual pages) in the tree - only count nodes without children that have URLs
  const countLeafNodes = useMemo(() => {
    if (!treeData || treeData.length === 0) return 0;
    
    let count = 0;
    // Find all nodes that are leaf nodes (no children) and have URLs (actual pages)
    for (const node of treeData) {
      const hasChildren = treeData.some(n => n.pid === node.id);
      if (!hasChildren && node.url) {
        count++;
      }
    }
    return count;
  }, [treeData]);

  // Hide sidebar if only one menu item is visible
  const shouldHideSidebar = countLeafNodes === 1;

  useEffect(() => {
    if (!treeData || treeData.length === 0) return;

    // Remove leading slash for comparison
    const currentPath = location.pathname.replace(/^\//, '');
    
    // Find the node that matches the current path
    const matchedNode = treeData.find((node) => {
      if (!node.url) return false;
      // Remove leading slash from node.url for comparison
      const nodePath = node.url.replace(/^\//, '');
      return nodePath === currentPath;
    });

    if (matchedNode) {
      setSelectedNodeId(matchedNode.id);
    } else {
      setSelectedNodeId(null);
    }
  }, [treeData, currentPerspective, location.pathname, setSelectedNodeId]);

  const handlePerspectiveChange = (key: string) => {
    if (key !== lastPerspective.current) {
      lastPerspective.current = null;
      navigate(`/${key}`);
    }
  };

  return (
    <div className="h-screen w-screen flex flex-col">
      {/* Header */}
      <Header
        perspectives={visiblePerspectives}
        currentPerspective={currentPerspective}
        onPerspectiveChange={handlePerspectiveChange}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        showSidebarToggle={!shouldHideSidebar}
      />

      <LicenseNotice />

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar - Hide if only one menu item is visible */}
        {!shouldHideSidebar && <Sidebar sidebarOpen={sidebarOpen} />}

        {/* Main Area - overflow-y-auto so long pages (e.g. System Settings) can scroll */}
        <main className={`main-content flex-1 transition-all min-h-0 overflow-y-auto ${shouldHideSidebar ? 'w-full' : ''}`}>
          {children}
        </main>
      </div>
    </div>
  );
}
